import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  Truck, User, AlertTriangle, FileText, Shield, CheckCircle,
  Clock, XCircle, Loader2, TrendingUp
} from 'lucide-react';
import {
  PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar,
  XAxis, YAxis, Tooltip, Legend
} from 'recharts';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COLORS = ['#1db584', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899'];

const STATUS_LABELS = {
  disponible: 'Disponible', occupe: 'Occupe', en_mission: 'En mission',
  inactif: 'Inactif', maintenance: 'Maintenance', en_panne: 'En panne'
};
const TYPE_LABELS = {
  plateau: 'Plateau', fourgon: 'Fourgon', citerne: 'Citerne', benne: 'Benne',
  porte_conteneur: 'Porte-cont.', fourgon_frigo: 'Frigo', autre: 'Autre'
};

const StatCard = ({ icon: Icon, iconColor, label, value, sub, alert }) => (
  <Card className={alert ? 'border-red-200 bg-red-50/30' : ''} data-testid={`fleet-stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <CardContent className="p-4 flex items-center gap-3">
      <div className={`w-10 h-10 rounded-lg ${alert ? 'bg-red-100' : 'bg-gray-100'} flex items-center justify-center shrink-0`}>
        <Icon className={`w-5 h-5 ${iconColor || 'text-gray-600'}`} />
      </div>
      <div>
        <p className="text-2xl font-bold text-gray-900">{value}</p>
        <p className="text-xs text-gray-500">{label}</p>
        {sub && <p className="text-[10px] text-gray-400 mt-0.5">{sub}</p>}
      </div>
    </CardContent>
  </Card>
);

const FleetKPIs = ({ token }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/fleet-kpis`, {});
        setData(res.data);
      } catch { /* ignore */ }
      setLoading(false);
    };
    fetch();
  }, [token]);

  if (loading) return <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>;
  if (!data) return <div className="text-center py-16 text-gray-400">Impossible de charger les KPIs flotte</div>;

  const v = data.vehicles;
  const d = data.drivers;
  const exp = data.expiring;
  const docs = data.documents;

  const vehicleStatusData = Object.entries(v.byStatus).map(([k, val]) => ({ name: STATUS_LABELS[k] || k, value: val }));
  const vehicleTypeData = Object.entries(v.byType).map(([k, val]) => ({ name: TYPE_LABELS[k] || k, value: val }));
  const driverStatusData = Object.entries(d.byStatus).map(([k, val]) => ({ name: STATUS_LABELS[k] || k, value: val }));

  const totalExpiring = exp.permitsExpired + exp.permitsExpiring15 + exp.permitsExpiring30 + exp.identityExpired + exp.identityExpiring30;

  return (
    <div className="space-y-6" data-testid="fleet-kpis-tab">
      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon={Truck} iconColor="text-emerald-600" label="Vehicules" value={v.total}
          sub={`${v.assignedToPartner} assignes · ${v.unassigned} libres`} />
        <StatCard icon={User} iconColor="text-blue-600" label="Chauffeurs" value={d.total}
          sub={`${d.byStatus?.disponible || 0} disponibles · ${d.byStatus?.occupe || 0} occupes`} />
        <StatCard icon={FileText} iconColor="text-violet-600" label="Documents OCR" value={docs.total}
          sub={`${docs.vehicle} vehicule · ${docs.driver} chauffeur`} />
        <StatCard icon={AlertTriangle} iconColor="text-red-600" label="Alertes expiration" value={totalExpiring}
          alert={totalExpiring > 0}
          sub={totalExpiring > 0 ? `${exp.permitsExpired + exp.identityExpired} expires` : 'Tout est a jour'} />
      </div>

      {/* Expiration Alerts Detail */}
      {totalExpiring > 0 && (
        <Card className="border-red-200" data-testid="fleet-expiration-alerts">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-sm text-red-700">
              <AlertTriangle className="w-4 h-4" />
              Alertes d'expiration
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {exp.permitsExpired > 0 && (
                <AlertBadge label="Permis expires" count={exp.permitsExpired} severity="critical" />
              )}
              {exp.permitsExpiring15 > 0 && (
                <AlertBadge label="Permis < 15j" count={exp.permitsExpiring15} severity="warning" />
              )}
              {exp.permitsExpiring30 > 0 && (
                <AlertBadge label="Permis < 30j" count={exp.permitsExpiring30} severity="info" />
              )}
              {exp.identityExpired > 0 && (
                <AlertBadge label="PI expirees" count={exp.identityExpired} severity="critical" />
              )}
              {exp.identityExpiring30 > 0 && (
                <AlertBadge label="PI < 30j" count={exp.identityExpiring30} severity="info" />
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Charts */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Vehicle by status */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Véhicules par statut</CardTitle>
          </CardHeader>
          <CardContent>
            {vehicleStatusData.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={vehicleStatusData} cx="50%" cy="50%" innerRadius={50} outerRadius={80}
                    paddingAngle={3} dataKey="value">
                    {vehicleStatusData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(val) => [val, '']} />
                  <Legend iconSize={8} wrapperStyle={{ fontSize: '11px' }} />
                </PieChart>
              </ResponsiveContainer>
            ) : <p className="text-xs text-gray-400 text-center py-8">Aucune donnee</p>}
          </CardContent>
        </Card>

        {/* Vehicle by type */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Véhicules par type</CardTitle>
          </CardHeader>
          <CardContent>
            {vehicleTypeData.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={vehicleTypeData} layout="vertical" margin={{ left: 10 }}>
                  <XAxis type="number" tick={{ fontSize: 10 }} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={70} />
                  <Tooltip />
                  <Bar dataKey="value" fill="#1db584" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : <p className="text-xs text-gray-400 text-center py-8">Aucune donnee</p>}
          </CardContent>
        </Card>

        {/* Drivers by status */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Chauffeurs par statut</CardTitle>
          </CardHeader>
          <CardContent>
            {driverStatusData.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={driverStatusData} cx="50%" cy="50%" innerRadius={50} outerRadius={80}
                    paddingAngle={3} dataKey="value">
                    {driverStatusData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(val) => [val, '']} />
                  <Legend iconSize={8} wrapperStyle={{ fontSize: '11px' }} />
                </PieChart>
              </ResponsiveContainer>
            ) : <p className="text-xs text-gray-400 text-center py-8">Aucune donnee</p>}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const AlertBadge = ({ label, count, severity }) => {
  const colors = {
    critical: 'bg-red-100 text-red-700 border-red-200',
    warning: 'bg-amber-100 text-amber-700 border-amber-200',
    info: 'bg-blue-100 text-blue-700 border-blue-200',
  };
  const icons = { critical: XCircle, warning: Clock, info: Shield };
  const Icon = icons[severity];
  return (
    <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${colors[severity]}`} data-testid={`alert-${label.replace(/\s/g, '-')}`}>
      <Icon className="w-4 h-4 shrink-0" />
      <div>
        <p className="text-lg font-bold leading-none">{count}</p>
        <p className="text-[10px]">{label}</p>
      </div>
    </div>
  );
};

export default FleetKPIs;
